/**
 * PortalConsent — the short-form consent step (AIMHI Direct).
 *
 * The three published documents run to ~1,900 words. Asking someone to read
 * all of that and tick three boxes before they have met anyone does not
 * produce informed consent — it produces three unread ticks, and it buries the
 * document that matters most behind two that matter less.
 *
 * So this screen asks the client to affirm eight plain-language points, each
 * linked to the full document it comes from. The documents remain the
 * canonical versioned text and are what the consent record stamps; these
 * answers are the evidence the client understood them.
 *
 * Three answers, not a tick box: agreeing, not following, and refusing are
 * different things and need different responses. "I don't follow this" is not
 * a blocker — there is a human appointment days away, and an unclear point
 * becomes something to raise there rather than a wall at the front door.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, CircularProgress, Stack,
  Divider, Chip, ToggleButton, ToggleButtonGroup, Collapse, Link as MuiLink
} from '@mui/material';
import {
  CheckCircle as CheckIcon, Gavel as GavelIcon,
  ExpandMore as ExpandIcon, ExpandLess as CollapseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const AGREE = 'understand_and_agree';
const UNCLEAR = 'do_not_understand';
const REFUSE = 'understand_and_do_not_agree';

export default function PortalConsent() {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [openDoc, setOpenDoc] = useState(null);
  // Which document the open panel shows. Starts as the point's own document;
  // the documents refer to each other, so the reader can switch to the others
  // (ST-20260925-0001).
  const [shownDocKey, setShownDocKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [outcome, setOutcome] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/consent/status', token);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.msg || 'Could not load the consent form');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const clauses = data?.clauses || [];
  const documents = data?.documents || [];
  const docFor = (key) => documents.find(d => d.documentKey === key);
  const answered = clauses.length > 0 && clauses.every(c => answers[c.id]);
  const anyRefused = clauses.some(c => answers[c.id] === REFUSE);
  const unclearCount = clauses.filter(c => answers[c.id] === UNCLEAR).length;

  const submit = async () => {
    try {
      setSaving(true);
      setError('');
      const res = await api.post('/consent/accept', { clauseResponses: answers }, token);
      setOutcome(res.data);
      if (res.data.complete) window.location.assign('/portal');
    } catch (err) {
      setError(err.response?.data?.msg || 'Could not record your answers');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress size={24} /></Box>;

  if (data && !data.configured) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Not ready yet</Typography>
        The forms for this programme have not been published. Your place is not affected — please
        check back shortly.
      </Alert>
    );
  }

  if (data?.complete && !outcome) {
    return (
      <Card sx={{ m: { xs: 0, sm: 2 } }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <CheckIcon color="success" />
            <Typography variant="h6">That is all agreed</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You can look back at what you agreed to, and when, from your settings at any time.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/portal')}>Continue</Button>
        </CardContent>
      </Card>
    );
  }

  // Submitted with something outstanding — say what happens next rather than
  // leaving the client stuck on a form they cannot complete.
  if (outcome && !outcome.complete) {
    return (
      <Card sx={{ m: { xs: 0, sm: 2 } }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Thanks — we have noted that</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {anyRefused
              ? 'You have said there is something here you do not agree to. That is entirely your right, and it does not end the conversation.'
              : 'You have flagged something you would like explained.'}
            {' '}Your therapist will go through it with you at your first appointment, before anything starts.
          </Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {(outcome.pointsToDiscuss || []).map(p => (
              <Alert key={p.clauseId} severity="info" icon={false}>
                <Typography variant="body2">{p.text}</Typography>
              </Alert>
            ))}
          </Stack>
          <Button variant="contained" onClick={() => navigate('/portal')}>
            Continue and book my first appointment
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ maxWidth: 780 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <GavelIcon color="primary" />
        <Typography variant="h5">Before we start</Typography>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Here is what taking part means, in plain terms. Please answer each one honestly — if
        something is not clear, say so and your therapist will go through it with you. You can read
        the full wording behind any point.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack spacing={2}>
        {clauses.map((c, i) => {
          const doc = docFor(c.documentKey);
          const isOpen = openDoc === c.id;
          const shownDoc = (isOpen && docFor(shownDocKey)) || doc;
          const otherDocs = shownDoc ? documents.filter(d => d.documentKey !== shownDoc.documentKey) : [];
          return (
            <Card key={c.id} variant="outlined">
              <CardContent sx={{ pb: 1.5 }}>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  <strong>{i + 1}.</strong> {c.text}
                </Typography>

                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={answers[c.id] || null}
                  onChange={(_, v) => v && setAnswers(a => ({ ...a, [c.id]: v }))}
                  sx={{ flexWrap: 'wrap' }}
                >
                  <ToggleButton value={AGREE} color="success">I understand and agree</ToggleButton>
                  <ToggleButton value={UNCLEAR}>I'm not sure what this means</ToggleButton>
                  <ToggleButton value={REFUSE} color="error">I don't agree</ToggleButton>
                </ToggleButtonGroup>

                {doc && (
                  <Box sx={{ mt: 1 }}>
                    <MuiLink
                      component="button"
                      variant="caption"
                      underline="hover"
                      onClick={() => {
                        setOpenDoc(isOpen ? null : c.id);
                        setShownDocKey(c.documentKey);
                      }}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {isOpen ? <CollapseIcon fontSize="inherit" /> : <ExpandIcon fontSize="inherit" />}
                      {isOpen ? 'Hide' : `Read the full wording — ${doc.title}`}
                    </MuiLink>
                    <Collapse in={isOpen}>
                      <Box sx={{
                        maxHeight: 300, overflowY: 'auto', p: 2, mt: 1,
                        bgcolor: 'action.hover', borderRadius: 1,
                        whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: 1.6
                      }}>
                        {shownDoc.body}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {shownDoc.title} · version {shownDoc.version}
                      </Typography>
                      {otherDocs.length > 0 && (
                        <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                          Also read:{' '}
                          {otherDocs.map((d, idx) => (
                            <React.Fragment key={d.documentKey}>
                              {idx > 0 && ' · '}
                              <MuiLink
                                component="button"
                                variant="caption"
                                underline="hover"
                                onClick={() => setShownDocKey(d.documentKey)}
                              >
                                {d.title}
                              </MuiLink>
                            </React.Fragment>
                          ))}
                        </Typography>
                      )}
                    </Collapse>
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <Divider sx={{ my: 3 }} />

      {unclearCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have flagged {unclearCount} point{unclearCount === 1 ? '' : 's'} to go through. That is
          fine — you can still book your first appointment, and your therapist will explain
          {unclearCount === 1 ? ' it' : ' them'} before anything starts.
        </Alert>
      )}
      {anyRefused && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have said you do not agree to something. You can still book and talk it through with
          your therapist — nothing starts until you are happy.
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="contained" size="large" onClick={submit} disabled={!answered || saving}>
          {saving ? 'Saving…' : 'Continue'}
        </Button>
        {!answered && (
          <Typography variant="body2" color="text.secondary">
            {clauses.length - Object.keys(answers).length} left to answer.
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Chip size="small" variant="outlined" label={`${Object.keys(answers).length} of ${clauses.length}`} />
      </Stack>
    </Box>
  );
}
